import {
  Box,
  Card,
  CardContent,
  alpha,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Grid,
  Icon,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { theme } from "../../../../../theme";
import { Permission } from "../UserManagement.interface";
import {
  userManagementService,
  Role,
} from "../../../../../services/userManagement.service";
import { toast } from "react-toastify";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { toLower } from "lodash";

// Simple startCase function to capitalize words
const startCase = (str: string): string => {
  return str
    .split("_")
    .map((word) => word.split(" "))
    .flat()
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

interface IRolesDefinitionProps {
  moduleKey: string;
  themeColor: string;
  accountingColors: any;
  systemPermissions: Array<{
    permission_code: string;
    module: string;
    group: string;
    action: string;
    description?: string;
    is_active: boolean;
  }>;
}

export const RolesDefinition: React.FC<IRolesDefinitionProps> = ({
  moduleKey,
  themeColor,
  accountingColors,
  systemPermissions,
}) => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState<{
    role_name: string;
    description: string;
    permissions: string[];
  }>({
    role_name: "",
    description: "",
    permissions: [],
  });

  // Track pending permission changes for roles
  const [pendingChanges, setPendingChanges] = useState<{
    [roleId: string]: {
      permissions: string[];
    };
  }>({});

  // Helper function to get API module name
  const getAPIModuleName = (moduleKey: string): string => {
    const moduleMap: { [key: string]: string } = {
      hr: "HR",
      accounting: "Accounting",
      inventory: "Inventory",
      pos: "POS",
    };
    return moduleMap[moduleKey] || moduleKey;
  };

  // Helper function to convert API permission to user format
  const convertAPIPermissionToUserFormat = (
    apiPermission: string,
    moduleKey: string
  ): string => {
    const modulePrefix = `${moduleKey.toLowerCase()}.`;
    return apiPermission.startsWith(modulePrefix)
      ? apiPermission.substring(modulePrefix.length)
      : apiPermission;
  };

  // Helper function to convert user permission to API format
  const convertUserPermissionToAPIFormat = (
    userPermission: string,
    moduleKey: string
  ): string => {
    if (
      userPermission.includes(".") &&
      !userPermission.startsWith(moduleKey.toLowerCase())
    ) {
      return `${moduleKey.toLowerCase()}.${userPermission}`;
    }
    return userPermission;
  };

  // Process real permissions data and group by category
  const processRealPermissions = (): Record<string, Permission[]> => {
    if (!systemPermissions || systemPermissions.length === 0) {
      return {};
    }

    // Filter permissions for current module
    const modulePermissions = systemPermissions.filter(
      (perm) =>
        perm.module.toLowerCase() === moduleKey.toLowerCase() && perm.is_active
    );

    // Group by "group" field (which becomes our category)
    const grouped = modulePermissions.reduce((acc, permission) => {
      const category = startCase(permission.group);

      if (!acc[category]) {
        acc[category] = [];
      }

      acc[category].push({
        id: permission.permission_code,
        name: startCase(permission.action),
        description:
          permission.description || `${permission.action} ${permission.group}`,
        category: category,
      });

      return acc;
    }, {} as Record<string, Permission[]>);

    return grouped;
  };

  const groupedPermissions = processRealPermissions();

  // Create permission modules structure
  const permissionModules = Object.entries(groupedPermissions).map(
    ([category, permissions]) => ({
      key: category.toLowerCase(),
      label: category,
      icon: getIconForCategory(category),
      color: getCategoryColor(category),
      permissions: permissions.map((p) => p.id),
    })
  );

  function getIconForCategory(category: string): string {
    const iconMap: Record<string, string> = {
      Dashboard: "dashboard",
      Employees: "people",
      Payroll: "payments",
      Attendance: "schedule",
      Accounts: "account_tree",
      Journals: "book",
      Vouchers: "receipt",
      Reports: "table_rows",
      Products: "inventory",
      Stock: "warehouse",
      Orders: "shopping_cart",
      Sales: "point_of_sale",
      Returns: "keyboard_return",
      Management: "admin_panel_settings",
      Coa: "account_tree",
      "Chart Of Accounts": "account_tree",
    };
    return iconMap[category] || "settings";
  }

  function getCategoryColor(category: string): string {
    const colorMap: Record<string, string> = {
      Dashboard: "#8B5CF6",
      Employees: "#F59E0B",
      Payroll: "#10B981",
      Attendance: "#06B6D4",
      Accounts: "#EF4444",
      Journals: "#8B5CF6",
      Vouchers: "#F59E0B",
      Reports: "#10B981",
      Products: "#06B6D4",
      Stock: "#EF4444",
      Orders: "#8B5CF6",
      Sales: "#F59E0B",
      Returns: "#10B981",
      Management: "#06B6D4",
      Coa: "#EF4444",
      "Chart Of Accounts": "#EF4444",
    };
    return colorMap[category] || "#6B7280";
  }

  // Get all available permissions for the module
  const getAllModulePermissions = (): Permission[] => {
    return Object.values(groupedPermissions).flat();
  };

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await userManagementService.listRoles({
        module: toLower(getAPIModuleName(moduleKey)),
        pageSize: 1000, // Get all roles
      });

      if (response.success && response.data?.data) {
        // Sort roles by creation date (oldest to newest)
        const sortedRoles = response.data.data.sort((a, b) => {
          const aCreatedAt = a.created_at || 0;
          const bCreatedAt = b.created_at || 0;
          return aCreatedAt - bCreatedAt;
        });
        setRoles(sortedRoles);
        // Clear pending changes when fetching fresh data
        setPendingChanges({});
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error("Failed to load roles");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (role?: Role) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        role_name: role.role_name,
        description: role.description || "",
        permissions: role.permissions.map((p) =>
          convertAPIPermissionToUserFormat(p, moduleKey)
        ),
      });
    } else {
      setEditingRole(null);
      setFormData({
        role_name: "",
        description: "",
        permissions: [],
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingRole(null);
  };

  const handleSave = async () => {
    if (!formData.role_name.trim()) {
      toast.error("Role name is required");
      return;
    }

    setSaving(true);
    try {
      // Convert user format permissions back to API format
      const apiPermissions = formData.permissions.map((p) =>
        convertUserPermissionToAPIFormat(p, moduleKey)
      );

      if (editingRole) {
        // Update existing role
        const response = await userManagementService.updateRole({
          _id: editingRole._id!,
          role_name: formData.role_name,
          description: formData.description,
          permissions: apiPermissions,
        });

        if (response.success) {
          toast.success("Role updated successfully");
          fetchRoles();
          handleCloseDialog();
        } else {
          toast.error(response.message || "Failed to update role");
        }
      } else {
        // Create new role
        const response = await userManagementService.createRole({
          role_name: formData.role_name,
          description: formData.description,
          module: toLower(getAPIModuleName(moduleKey)),
          permissions: apiPermissions,
        });

        if (response.success) {
          toast.success("Role created successfully");
          fetchRoles();
          handleCloseDialog();
        } else {
          toast.error(response.message || "Failed to create role");
        }
      }
    } catch (error) {
      console.error("Error saving role:", error);
      toast.error(`Failed to ${editingRole ? "update" : "create"} role`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role: Role) => {
    if (!role._id) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete the role "${role.role_name}"?`
    );
    if (!confirmed) return;

    setDeleting(role._id);
    try {
      const response = await userManagementService.deleteRole(role._id);

      if (response.success) {
        toast.success("Role deleted successfully");
        fetchRoles();
      } else {
        toast.error(response.message || "Failed to delete role");
      }
    } catch (error) {
      console.error("Error deleting role:", error);
      toast.error("Failed to delete role");
    } finally {
      setDeleting(null);
    }
  };

  const roleHasPermission = (role: Role, permissionId: string): boolean => {
    // Check if there are pending changes for this role
    const roleId = role._id;
    if (roleId && pendingChanges[roleId]) {
      // Use pending permissions if they exist
      const userFormatPermissionId = convertAPIPermissionToUserFormat(
        permissionId,
        moduleKey
      );
      const pendingPermissions = pendingChanges[roleId].permissions.map((p) =>
        convertAPIPermissionToUserFormat(p, moduleKey)
      );
      return pendingPermissions.includes(userFormatPermissionId);
    }

    // Fall back to current role permissions
    const userFormatPermissions = role.permissions.map((p) =>
      convertAPIPermissionToUserFormat(p, moduleKey)
    );
    const userFormatPermissionId = convertAPIPermissionToUserFormat(
      permissionId,
      moduleKey
    );
    return userFormatPermissions.includes(userFormatPermissionId);
  };

  const handleQuickToggle = (roleId: string, permissionId: string) => {
    const role = roles.find((r) => r._id === roleId);
    if (!role) return;

    const userFormatPermissionId = convertAPIPermissionToUserFormat(
      permissionId,
      moduleKey
    );
    const hasPermission = roleHasPermission(role, permissionId);

    // Get current permissions (either from pending changes or original role)
    const currentPermissions =
      pendingChanges[roleId]?.permissions || role.permissions;

    let updatedPermissions: string[];
    if (hasPermission) {
      // Remove permission (in API format)
      updatedPermissions = currentPermissions.filter((p) => {
        const userFormat = convertAPIPermissionToUserFormat(p, moduleKey);
        return userFormat !== userFormatPermissionId;
      });
    } else {
      // Add permission (in API format)
      const apiFormat = convertUserPermissionToAPIFormat(
        userFormatPermissionId,
        moduleKey
      );
      updatedPermissions = [...currentPermissions, apiFormat];
    }

    // Update pending changes
    setPendingChanges((prev) => ({
      ...prev,
      [roleId]: {
        permissions: updatedPermissions,
      },
    }));
  };

  const getRolePermissionCount = (role: Role) => {
    const allPermissions = getAllModulePermissions();
    const total = allPermissions.length;
    const granted = allPermissions.filter((p) =>
      roleHasPermission(role, p.id)
    ).length;
    return { granted, total };
  };

  // Handle saving pending changes
  const handleSavePendingChanges = async () => {
    setSaving(true);
    try {
      const savePromises = Object.entries(pendingChanges).map(
        async ([roleId, changes]) => {
          const role = roles.find((r) => r._id === roleId);
          if (!role) return { success: false, roleId };

          try {
            const response = await userManagementService.updateRole({
              _id: roleId,
              permissions: changes.permissions,
            });

            if (response.success) {
              return { success: true, roleId, roleName: role.role_name };
            } else {
              throw new Error(response.message || "Failed to update role");
            }
          } catch (error) {
            return {
              success: false,
              roleId,
              roleName: role.role_name,
              error: error instanceof Error ? error.message : "Unknown error",
            };
          }
        }
      );

      const results = await Promise.all(savePromises);
      const successCount = results.filter((r) => r.success).length;
      const failedCount = results.length - successCount;

      if (failedCount === 0) {
        toast.success(
          `Successfully updated permissions for ${successCount} role${
            successCount !== 1 ? "s" : ""
          }`
        );
        setPendingChanges({});
        fetchRoles(); // Refresh to get latest data
      } else {
        toast.error(
          `${successCount} role${
            successCount !== 1 ? "s" : ""
          } updated successfully, ${failedCount} failed`
        );
        // Only clear successful changes
        const failedRoleIds = results
          .filter((r) => !r.success)
          .map((r) => r.roleId);
        setPendingChanges((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((roleId) => {
            if (!failedRoleIds.includes(roleId)) {
              delete updated[roleId];
            }
          });
          return updated;
        });
        fetchRoles(); // Refresh to get latest data
      }
    } catch (error) {
      console.error("Error saving pending changes:", error);
      toast.error("Failed to save role permission changes");
    } finally {
      setSaving(false);
    }
  };

  // Clear pending changes
  const handleClearPendingChanges = () => {
    setPendingChanges({});
    toast.info("Pending changes cleared");
  };

  const getPermissionIcon = (hasPermission: boolean) => {
    return hasPermission ? (
      <Icon sx={{ color: accountingColors.success, fontSize: 18 }}>
        check_circle
      </Icon>
    ) : (
      <Icon sx={{ color: accountingColors.error, fontSize: 18 }}>cancel</Icon>
    );
  };

  useEffect(() => {
    fetchRoles();
  }, [moduleKey]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, mb: 1, color: themeColor }}
          >
            Role Management - {moduleKey.toUpperCase()}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Create and manage user roles with specific permissions for{" "}
            {moduleKey}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            bgcolor: themeColor,
            "&:hover": {
              bgcolor: accountingColors.secondary,
            },
          }}
        >
          Add Role
        </Button>
      </Box>

      {/* Show message if no permissions available */}
      {Object.keys(groupedPermissions).length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            No permissions are available for the{" "}
            <strong>{moduleKey.toUpperCase()}</strong> module. Please configure
            permissions in the system before creating roles.
          </Typography>
        </Alert>
      ) : (
        <>
          {/* Role Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {roles.length === 0 ? (
              // Placeholder card when no roles exist
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    height: 280, // Match the approximate height of role cards
                    border: `2px dashed ${alpha(themeColor, 0.3)}`,
                    backgroundColor: alpha(themeColor, 0.02),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 2,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      borderColor: alpha(themeColor, 0.5),
                      backgroundColor: alpha(themeColor, 0.05),
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      py: 6,
                    }}
                  >
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        backgroundColor: alpha(themeColor, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 3,
                      }}
                    >
                      <AddIcon
                        sx={{
                          fontSize: 28,
                          color: alpha(themeColor, 0.7),
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "text.secondary",
                        mb: 3,
                        fontWeight: 500,
                      }}
                    >
                      No roles created yet
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenDialog()}
                      sx={{
                        bgcolor: themeColor,
                        color: "white",
                        fontWeight: 600,
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        boxShadow: `0 4px 12px ${alpha(themeColor, 0.3)}`,
                        "&:hover": {
                          bgcolor: accountingColors.secondary,
                          boxShadow: `0 6px 16px ${alpha(themeColor, 0.4)}`,
                          transform: "translateY(-1px)",
                        },
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      Add Role
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ) : (
              roles.map((role) => {
                const { granted, total } = getRolePermissionCount(role);
                const percentage =
                  total > 0 ? Math.round((granted / total) * 100) : 0;

                return (
                  <Grid item xs={12} sm={6} md={4} key={role._id}>
                    <Card
                      sx={{
                        cursor: "pointer",
                        position: "relative",
                        border: pendingChanges[role._id!]
                          ? `2px solid ${accountingColors.warning}`
                          : "1px solid",
                        borderColor: pendingChanges[role._id!]
                          ? accountingColors.warning
                          : "divider",
                        "&:hover": {
                          boxShadow: `0 4px 12px ${alpha(themeColor, 0.15)}`,
                        },
                      }}
                    >
                      <CardContent>
                        {/* Pending Changes Badge */}
                        {pendingChanges[role._id!] && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              zIndex: 1,
                            }}
                          >
                            <Chip
                              label="Unsaved"
                              size="small"
                              sx={{
                                bgcolor: "warning.main",
                                color: "white",
                                fontSize: "0.7rem",
                                height: 20,
                                fontWeight: 600,
                              }}
                            />
                          </Box>
                        )}

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 2,
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 600, mb: 0.5 }}
                            >
                              {role.role_name}
                            </Typography>
                            {role.description && (
                              <Typography
                                variant="body2"
                                sx={{ color: "text.secondary", mb: 1 }}
                              >
                                {role.description}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <IconButton
                              size="small"
                              sx={{ color: themeColor }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDialog(role);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              sx={{ color: "error.main" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(role);
                              }}
                              disabled={deleting === role._id}
                            >
                              {deleting === role._id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Box>
                        </Box>

                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 700, color: themeColor, mb: 1 }}
                        >
                          {percentage}%
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", mb: 2 }}
                        >
                          {granted} of {total} permissions
                        </Typography>

                        <Box
                          sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                        >
                          {permissionModules.map((module) => {
                            const hasAnyPermission = module.permissions.some(
                              (permissionId: string) =>
                                roleHasPermission(role, permissionId)
                            );
                            return (
                              <Chip
                                key={module.key}
                                size="small"
                                label={module.label}
                                color={hasAnyPermission ? "primary" : "default"}
                                variant={
                                  hasAnyPermission ? "filled" : "outlined"
                                }
                                sx={{
                                  borderColor: hasAnyPermission
                                    ? themeColor
                                    : undefined,
                                  backgroundColor: hasAnyPermission
                                    ? themeColor
                                    : undefined,
                                }}
                              />
                            );
                          })}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })
            )}
          </Grid>

          {/* Save Pending Changes Button */}
          {Object.keys(pendingChanges).length > 0 && (
            <Card sx={{ mb: 3, borderColor: "warning.main", borderWidth: 2 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: "warning.main", mb: 0.5 }}
                    >
                      Unsaved Changes
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      You have {Object.keys(pendingChanges).length} role
                      {Object.keys(pendingChanges).length !== 1 ? "s" : ""} with
                      pending permission changes
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={handleClearPendingChanges}
                      disabled={saving}
                      sx={{
                        borderColor: "warning.main",
                        color: "warning.main",
                        "&:hover": {
                          borderColor: "warning.dark",
                          backgroundColor: alpha(themeColor, 0.05),
                        },
                      }}
                    >
                      Clear Changes
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={
                        saving ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <SaveIcon />
                        )
                      }
                      onClick={handleSavePendingChanges}
                      disabled={saving}
                      sx={{
                        bgcolor: "success.main",
                        color: "white",
                        fontWeight: 600,
                        "&:hover": {
                          bgcolor: "success.dark",
                        },
                      }}
                    >
                      {saving ? "Saving..." : "Save Permission Changes"}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Permissions Matrix */}
          {roles.length > 0 && (
            <Card>
              <CardContent sx={{ p: 0 }}>
                <Box
                  sx={{
                    p: 3,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: themeColor }}
                  >
                    Permissions Matrix
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Click on permission cells to toggle access quickly
                  </Typography>
                </Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table sx={{ minWidth: 800 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(themeColor, 0.05) }}>
                        <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>
                          Role
                        </TableCell>
                        {permissionModules.map((module) => (
                          <TableCell
                            key={module.key}
                            align="center"
                            sx={{ fontWeight: 600 }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                              }}
                            >
                              <Icon sx={{ color: module.color, fontSize: 18 }}>
                                {module.icon}
                              </Icon>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                              >
                                {module.label}
                              </Typography>
                            </Box>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {roles.map((role) => (
                        <TableRow
                          key={role._id}
                          sx={{
                            backgroundColor: pendingChanges[role._id!]
                              ? alpha("warning.main", 0.1)
                              : "transparent",
                            borderLeft: pendingChanges[role._id!]
                              ? `4px solid ${accountingColors.warning}`
                              : "4px solid transparent",
                          }}
                        >
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {role.role_name}
                                </Typography>
                                {role.description && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "text.secondary",
                                      display: "block",
                                    }}
                                  >
                                    {role.description}
                                  </Typography>
                                )}
                              </Box>
                              {pendingChanges[role._id!] && (
                                <Chip
                                  label="Unsaved"
                                  size="small"
                                  sx={{
                                    bgcolor: "warning.main",
                                    color: "white",
                                    fontSize: "0.6rem",
                                    height: 18,
                                    fontWeight: 600,
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          {permissionModules.map((module) => (
                            <TableCell key={module.key} align="center">
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 0.5,
                                }}
                              >
                                {module.permissions.map(
                                  (permissionId: string) => {
                                    const permission =
                                      getAllModulePermissions().find(
                                        (p) => p.id === permissionId
                                      );
                                    const roleHasPermissionValue =
                                      roleHasPermission(role, permissionId);
                                    return (
                                      <Box
                                        key={permissionId}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "space-between",
                                          gap: 1,
                                          p: 0.5,
                                          borderRadius: 0.5,
                                          cursor: "pointer",
                                          "&:hover": {
                                            bgcolor: alpha(themeColor, 0.1),
                                          },
                                        }}
                                        onClick={() =>
                                          handleQuickToggle(
                                            role._id!,
                                            permissionId
                                          )
                                        }
                                      >
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            textTransform: "capitalize",
                                            fontSize: "0.7rem",
                                          }}
                                        >
                                          {permission?.name || "Unknown"}
                                        </Typography>
                                        {getPermissionIcon(
                                          roleHasPermissionValue
                                        )}
                                      </Box>
                                    );
                                  }
                                )}
                              </Box>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Add/Edit Role Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ color: themeColor }}>
          {editingRole ? "Edit Role" : "Add New Role"}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Role Name"
            value={formData.role_name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, role_name: e.target.value }))
            }
            sx={{ mt: 2, mb: 3 }}
            placeholder="e.g., Senior Manager"
            required
          />

          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
            sx={{ mb: 3 }}
            placeholder="e.g., Full access to all management features"
            multiline
            rows={2}
          />

          <Typography
            variant="h6"
            sx={{ fontWeight: 600, mb: 2, color: themeColor }}
          >
            Permissions
          </Typography>

          {permissionModules.map((module) => (
            <Card key={module.key} sx={{ mb: 2 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Icon sx={{ color: module.color }}>{module.icon}</Icon>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {module.label}
                  </Typography>
                </Box>
                <Grid container spacing={1}>
                  {module.permissions.map((permissionId: string) => {
                    const permission = getAllModulePermissions().find(
                      (p) => p.id === permissionId
                    );
                    const userFormatPermissionId =
                      convertAPIPermissionToUserFormat(permissionId, moduleKey);
                    const isChecked = formData.permissions.includes(
                      userFormatPermissionId
                    );

                    return (
                      <Grid item xs={6} sm={4} md={3} key={permissionId}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData((prev) => ({
                                    ...prev,
                                    permissions: [
                                      ...prev.permissions,
                                      userFormatPermissionId,
                                    ],
                                  }));
                                } else {
                                  setFormData((prev) => ({
                                    ...prev,
                                    permissions: prev.permissions.filter(
                                      (p) => p !== userFormatPermissionId
                                    ),
                                  }));
                                }
                              }}
                              size="small"
                              sx={{
                                color: themeColor,
                                "&.Mui-checked": {
                                  color: themeColor,
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              sx={{ textTransform: "capitalize" }}
                            >
                              {permission?.name || "Unknown"}
                            </Typography>
                          }
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.role_name.trim() || saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={{
              bgcolor: themeColor,
              "&:hover": {
                bgcolor: accountingColors.secondary,
              },
            }}
          >
            {saving ? "Saving..." : editingRole ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
